import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AdminActions.css';

const AdminActions = () => {
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    isNewProduct: 'false',
    rating: '0',
    reviewCount: '0'
  });
  
  // State for product image
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // State for products list
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  // State for UI controls
  const [activeTab, setActiveTab] = useState('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);

  // Categories for dropdown
  const categories = ['Sıcak İçecekler', 'Soğuk İçecekler', 'Tatlılar', 'Atıştırmalıklar', 'Ana Yemekler'];

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('dosya boyutu 5MB\'dan küçük olmalıdır!');
        return;
      }
      
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error('sadece png ve jpg formatı kabul edilir!');
        return;
      }
      
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Fetch all products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/prods/getproducts`,);
      setProducts(response.data.items);
    } catch (error) {
      toast.error('Failed to fetch products!');
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!image) {
      toast.error('Lütfen resim seçin!');
      return;
    }
    
    if (!formData.name || !formData.description || !formData.price || !formData.category) {
      toast.error('Lütfen heryeri doldurun!');
      return;
    }
    
    setFormLoading(true);
    
    // Create form data for submission
    const productData = new FormData();
    productData.append('image', image);
    Object.keys(formData).forEach(key => {
      productData.append(key, formData[key]);
    });
    
    try {
      const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/prods/createproduct`, productData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Ürün başarıyla oluşturuldu!');
      // Reset form
      setFormData({
        name: '',
        description: '',
        price: '',
        category: '',
        isNewProduct: 'false',
        rating: '0',
        reviewCount: '0'
      });
      setImage(null);
      setImagePreview(null);
      
      // Refresh products list if we're viewing it
      if (activeTab === 'view') {
        fetchProducts();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Ürün oluşturulurken hata oldu!';
      toast.error(errorMsg);
      console.error('Hata oldu:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      isNewProduct: product.isNew ? 'true' : 'false',
      rating: product.rating.toString(),
      reviewCount: product.reviewCount.toString()
    });
    setImagePreview(product.imageUrl);
    setActiveTab('create'); // Switch to the create tab for editing
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/prods/deleteproduct/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ürün başarıyla silindi!');
      fetchProducts(); // Refresh the product list
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Ürün silinirken hata oldu!';
      toast.error(errorMsg);
      console.error('Hata oldu:', error);
    }
  };

  // Load products when component mounts or tab changes
  useEffect(() => {
    if (activeTab === 'view') {
      fetchProducts();
    }
  }, [activeTab]);

  // Filter products based on search term
  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-actions-container min-h-screen bg-gray-50 p-4">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <h1 className="text-3xl font-bold text-center mb-8 text-primary animate-fadeIn">
        Admin Ürün Yönetimi
      </h1>
      
      {/* Tab Navigation */}
      <div className="tab-navigation mb-6 flex justify-center">
        <button 
          className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Ürün Ekle
        </button>
        <button 
          className={`tab-button ${activeTab === 'view' ? 'active' : ''}`}
          onClick={() => setActiveTab('view')}
        >
          Ürünler
        </button>
      </div>
      
      {/* Create Product Form */}
      {activeTab === 'create' && (
        <div className="create-product-form animate-slideUp">
          <h2 className="text-2xl font-semibold mb-6 text-center">Yeni ürün ekle</h2>
          
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="name">Ürün ismi*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter product name"
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="category">Kategori*</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="price">Fiyat (TL)*</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="Enter price"
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="isNewProduct">Yeni ürünmü?</label>
                <select
                  id="isNewProduct"
                  name="isNewProduct"
                  value={formData.isNewProduct}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="false">Evet</option>
                  <option value="true">Hayır</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="rating">İlk derecelendirme (0-5)</label>
                <input
                  type="number"
                  id="rating"
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                  min="0"
                  max="5"
                  step="0.1"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="reviewCount">İlk inceleme sayısı</label>
                <input
                  type="number"
                  id="reviewCount"
                  name="reviewCount"
                  value={formData.reviewCount}
                  onChange={handleInputChange}
                  min="0"
                  className="form-input"
                />
              </div>
              
              <div className="form-group md:col-span-2">
                <label htmlFor="description">Açıklama*</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter product description"
                  className="form-input min-h-[120px]"
                  required
                />
              </div>
              
              <div className="form-group md:col-span-2">
                <label htmlFor="image">Ürün resmi*</label>
                <div className="image-upload-container">
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleImageChange}
                    accept="image/png, image/jpeg, image/jpg"
                    className="hidden"
                  />
                  <label htmlFor="image" className="image-upload-label">
                    <div className="image-upload-button">
                      <span>Resim seçin</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="12" y1="18" x2="12" y2="12"></line>
                        <line x1="9" y1="15" x2="15" y2="15"></line>
                      </svg>
                    </div>
                  </label>
                </div>
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Product preview" />
                    <button 
                      type="button" 
                      className="remove-image-btn"
                      onClick={() => {
                        setImage(null);
                        setImagePreview(null);
                      }}
                    >
                      Kaldır
                    </button>
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-1">En fazla: 5MB. Format: JPEG, PNG</p>
              </div>
            </div>
            
            <div className="form-footer">
              <button 
                type="submit" 
                className="submit-button"
                disabled={formLoading}
              >
                {formLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Oluşturuluyor...
                  </>
                ) : 'Ürün Ekle'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* View Products Section */}
      {activeTab === 'view' && (
        <div className="view-products animate-slideUp">
          <div className="flex justify-between items-center mb-6 flex-col sm:flex-row">
            <h2 className="text-2xl font-semibold mb-2 sm:mb-0">Ürün listesi</h2>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
          </div>
          
          {loading ? (
            <div className="loading-spinner">
              <svg className="animate-spin h-12 w-12" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <div key={product._id} className="product-card">
                  <div className="product-image-container">
                    <img src={product.imageUrl} alt={product.name} className="product-image" />
                    {product.isNew && <span className="new-badge">Yeni</span>}
                  </div>
                  <div className="product-details">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-category">{product.category}</p>
                    <p className="product-description">{product.description.length > 100 
                      ? `${product.description.substring(0, 100)}...` 
                      : product.description}
                    </p>
                    <div className="product-meta">
                      <div className="product-price">{product.price} TL</div>
                      <div className="product-rating">
                        <span>★ {product.rating}</span>
                        <span className="review-count">({product.reviewCount})</span>
                      </div>
                    </div>
                    <div className="product-actions">
                      <button 
                        className="edit-button"
                        onClick={() => handleEdit(product)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        Düzenle
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => setShowConfirmDelete(product._id)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                        Sil
                      </button>
                      {showConfirmDelete === product._id && (
                        <div className="delete-confirm">
                          <p>Emin misin?</p>
                          <div>
                            <button 
                              className="confirm-yes"
                              onClick={() => {
                                handleDelete(product._id);
                                setShowConfirmDelete(null);
                              }}
                            >
                              Evet
                            </button>
                            <button 
                              className="confirm-no"
                              onClick={() => setShowConfirmDelete(null)}
                            >
                              Hayır
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-products">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>Herhangi bir ürün bulunmadı{searchTerm ? ' matching your search' : ''}.</p>
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="clear-search-btn"
                >
                  Aramayı temizle
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminActions;
